import React, { useState, useEffect, useRef } from 'react'
import { OTPInput } from './OTPInput'
import { HiddenOTPInput } from './HiddenOTPInput'
import { addLog } from '../../components/LoggerUI'
import { callOTP } from '../../utils/appBridge'
import useWindowSize from '../../hooks/useWindowSize'

interface OTPInputContainerProps {
  numberOfDigits: number
  otp: string[]
  onOTPChange: (otp: string[], isOTPAutoFilled?: boolean) => void
  isError: boolean
  isDisabled: boolean
  resetFocus?: boolean
  handleResetFocus: () => void
  testLocator?: string
}

export const OTPInputContainer: React.FC<OTPInputContainerProps> = ({
  numberOfDigits,
  otp,
  onOTPChange,
  isDisabled,
  resetFocus = false,
  handleResetFocus,
  testLocator
}) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const { height } = useWindowSize()

  // Function to trigger focus on hidden input
  const triggerFocus = () => {
    const hiddenInput = document.getElementById('hidden-otp-input')
    if (hiddenInput) {
      hiddenInput.focus()
      addLog(`Focused on hidden input`, 'info')
    }
  }

  // Function to restore focus to the hidden input
  const restoreFocus = () => {
    triggerFocus()
    addLog(`Restored focus to hidden input`, 'info')
  }

  // Auto-focus next input when current is filled
  useEffect(() => {
    if (otp[activeIndex] && activeIndex < numberOfDigits - 1) {
      const nextIndex = activeIndex + 1
      setActiveIndex(nextIndex)
    }
  }, [otp, activeIndex, numberOfDigits])

  // Auto-focus hidden input when activeIndex changes
  useEffect(() => {
    triggerFocus()
  }, [activeIndex])

  // Auto-focus first input on mount or when resetFocus is true
  useEffect(() => {
    if (resetFocus) {
      setActiveIndex(0)
      triggerFocus()
      handleResetFocus()
    }
  }, [resetFocus])

  // Document click handler to manage focus behavior
  useEffect(() => {
    const handleDocumentClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      const otpContainer = target.closest('.otp-container')

      // If click is within OTP container, restore focus to hidden input
      if (otpContainer) {
        restoreFocus()
      }
      // If click is outside OTP container, let it blur naturally
    }

    document.addEventListener('click', handleDocumentClick)

    return () => {
      document.removeEventListener('click', handleDocumentClick)
    }
  }, [activeIndex])

  const handleInputFocus = (index: number) => {
    setActiveIndex(index)
    triggerFocus()
    addLog(`Focused on OTP input ${index + 1}`, 'info')
  }

  const handleAutofill = (digits: string[]) => {
    if (digits.length !== numberOfDigits) return

    // Update all OTP digits at once
    const newOtp = [...otp]
    digits.forEach((digit, index) => {
      if (index < numberOfDigits) {
        newOtp[index] = digit
      }
    })
    onOTPChange(newOtp, true)

    // Log the autofill
    addLog(`SMS autofill detected: ${digits.join('')}`, 'success')

    // Focus the last input after distribution
    setActiveIndex(numberOfDigits - 1)
    setTimeout(() => {
      triggerFocus()
    }, 0)
  }

  // Initial focus on mount
  useEffect(() => {
    triggerFocus()
  }, [])

  // Check for auto-fill OTP from session storage after page is ready
  useEffect(() => {
    callOTP()

    intervalRef.current = setInterval(() => {
      const autoFillOTP = sessionStorage.getItem('autoFillOTP')
      if (autoFillOTP && autoFillOTP.length === numberOfDigits && /^\d+$/.test(autoFillOTP)) {
        // Convert the OTP string to an array of digits
        const otpDigits = autoFillOTP.split('')
        addLog(`Auto-filled OTP from session storage: ${autoFillOTP}`, 'success')
        handleAutofill(otpDigits)
        sessionStorage.removeItem('autoFillOTP')

        // Clear the interval after successful auto-fill
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
        }
      }
    }, 100)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (height) {
      restoreFocus()
    }
  }, [height])

  return (
    <div test-locator={testLocator} className='flex gap-2 mb-2 otp-container relative'>
      {/* Hidden input that handles all keyboard input */}
      <HiddenOTPInput
        numberOfDigits={numberOfDigits}
        otp={otp}
        activeIndex={activeIndex}
        onOTPChange={onOTPChange}
        onActiveIndexChange={setActiveIndex}
        onAutofill={handleAutofill}
        isDisabled={isDisabled}
      />

      {/* Visual OTP inputs */}
      {otp?.map((digit, index) => (
        <OTPInput key={index} value={digit} isActive={activeIndex === index} index={index} />
      ))}
    </div>
  )
}
